import React from "react"
import {useTranslation} from "react-i18next"
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react"
import {CalendarIcon} from "@heroicons/react/24/outline"
import {addDays, format, min, parseISO} from "date-fns"
import {twMerge} from "tailwind-merge"

import Button from "../../../components/Button"
import {FieldLabel} from "../../../components/fields/FieldLabel"
import {DateBase} from "../../../components/formElements/DateField"
import {getDateFnsLocale} from "../../../utils/dateArithmetics"
import {
  customRangeDays,
  DEFAULT_KLARA_PERIOD,
  formatDay,
  getPeriodRange,
  getQuickRanges,
  MAX_CUSTOM_RANGE_DAYS,
  TKlaraPeriod,
  TQuickRange,
  validateCustomRange,
} from "../period"
import {formatRange} from "./periodLabel"

type TDraft = {from: string; to: string}

// Lifetime has no bounds to start from, so the draft falls back to the default window.
const seedDraft = (value: TKlaraPeriod): TDraft => {
  const {from, to} = getPeriodRange(value)
  if (from !== undefined && to !== undefined) {
    return {from, to}
  }
  return getPeriodRange(DEFAULT_KLARA_PERIOD) as TDraft
}

export const CustomRangePicker: React.FC<{value: TKlaraPeriod; onApply: (next: TKlaraPeriod) => void}> = ({
  value,
  onApply,
}) => {
  const {t} = useTranslation()
  const [isOpen, setIsOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<TDraft>(() => seedDraft(value))
  // DateBase opens its calendar on focus, so initial focus goes to the first quick pick instead.
  const firstQuickPickRef = React.useRef<HTMLButtonElement | null>(null)

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraft(seedDraft(value))
    }
    setIsOpen(open)
  }

  const {context, refs, floatingStyles} = useFloating({
    placement: "bottom-end",
    middleware: [flip(), offset(10), shift({padding: 16})],
    open: isOpen,
    onOpenChange: handleOpenChange,
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)

  const {getReferenceProps, getFloatingProps} = useInteractions([click, dismiss])
  const {styles} = useTransitionStyles(context, {
    duration: {open: 100, close: 75},
    initial: {opacity: 0, transform: "scale(0.95)"},
    open: {opacity: 1, transform: "scale(1)"},
    common: ({side}) => ({
      transformOrigin: {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      }[side],
    }),
  })

  const isActive = value.kind === "custom"
  const today = new Date()
  const fromDate = parseISO(draft.from)
  const toDate = parseISO(draft.to)
  const days = customRangeDays(draft.from, draft.to)
  const isValid = validateCustomRange(draft.from, draft.to, today)

  const quickRangeLabel = ({label}: TQuickRange) =>
    label.kind === "month"
      ? format(parseISO(label.date), "LLL yyyy", getDateFnsLocale())
      : t("KlaraDashboard_Period_Quarter", {quarter: label.quarter, year: label.year})

  const apply = (range: TDraft) => {
    onApply({kind: "custom", ...range})
    setIsOpen(false)
  }

  const handleFromChange = (date: Date) => {
    setDraft(prev => ({...prev, from: formatDay(date)}))
  }

  const handleToChange = (date: Date) => {
    setDraft(prev => ({...prev, to: formatDay(date)}))
  }

  return (
    <>
      <button
        ref={refs.setReference}
        type={"button"}
        aria-pressed={isActive}
        aria-expanded={isOpen}
        aria-haspopup={"dialog"}
        className={twMerge(
          "flex items-center gap-1.5 rounded-[7px] px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors",
          isActive ? "bg-cr-blue text-white" : "text-cr-slate-60 hover:text-cr-slate-90"
        )}
        {...getReferenceProps()}
      >
        <CalendarIcon className={"size-3.5 shrink-0"} aria-hidden={"true"} />
        {isActive ? formatRange(value.from, value.to) : t("KlaraDashboard_Period_Custom")}
      </button>

      {isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={firstQuickPickRef}>
            <div
              className={"absolute z-100"}
              ref={refs.setFloating}
              style={floatingStyles}
              role={"dialog"}
              aria-label={t("KlaraDashboard_Period_Custom")}
              {...getFloatingProps()}
            >
              <div
                style={{...styles}}
                className={"flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-4 floating-card pixel-units"}
              >
                <div className={"flex flex-col gap-2"}>
                  <span className={"text-xs font-semibold text-cr-grey-50"}>
                    {t("KlaraDashboard_Period_QuickPicks")}
                  </span>
                  <div className={"flex flex-wrap gap-2"}>
                    {getQuickRanges(today).map((range, index) => {
                      const isPicked = isActive && value.from === range.from && value.to === range.to
                      return (
                        <button
                          key={range.key}
                          ref={index === 0 ? firstQuickPickRef : undefined}
                          type={"button"}
                          aria-pressed={isPicked}
                          onClick={() => apply(range)}
                          className={twMerge(
                            "rounded-md border px-3 py-1 text-xs font-semibold transition-colors",
                            isPicked
                              ? "border-cr-blue bg-cr-blue text-white"
                              : "border-cr-grey-15 text-cr-grey-50 hover:text-cr-grey-90"
                          )}
                        >
                          {quickRangeLabel(range)}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className={"flex flex-col gap-3"}>
                  <FieldLabel
                    name={"klara-range-from"}
                    label={t("KlaraDashboard_Period_From")}
                    variant={"small"}
                    hideError
                  >
                    <DateBase
                      name={"klara-range-from"}
                      value={fromDate}
                      onChange={handleFromChange}
                      maxDate={min([toDate, today])}
                      calendarClassName={"w-full"}
                    />
                  </FieldLabel>
                  <FieldLabel name={"klara-range-to"} label={t("KlaraDashboard_Period_To")} variant={"small"} hideError>
                    <DateBase
                      name={"klara-range-to"}
                      value={toDate}
                      onChange={handleToChange}
                      minDate={fromDate}
                      maxDate={min([addDays(fromDate, MAX_CUSTOM_RANGE_DAYS - 1), today])}
                      calendarClassName={"w-full"}
                    />
                  </FieldLabel>
                </div>

                <div className={"flex flex-wrap items-center justify-between gap-3"}>
                  <div className={"flex flex-col text-xs"}>
                    <span className={"text-cr-grey-50"}>{t("KlaraDashboard_Period_DaysSelected", {count: days})}</span>
                    {days > MAX_CUSTOM_RANGE_DAYS && (
                      <span className={"text-cr-red"}>
                        {t("KlaraDashboard_Period_InvalidRange", {max: MAX_CUSTOM_RANGE_DAYS})}
                      </span>
                    )}
                  </div>
                  <div className={"flex gap-2"}>
                    <Button size={"sm"} variant={"outlined"} color={"gray"} onClick={() => setIsOpen(false)}>
                      {t("KlaraDashboard_Period_Cancel")}
                    </Button>
                    <Button size={"sm"} disabled={!isValid} onClick={() => apply(draft)}>
                      {t("KlaraDashboard_Period_Apply")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}
